import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { AlertTriangle, CheckCircle2, Pencil, Ban, Download, Mail } from 'lucide-react';

/**
 * CpscExemptionRecords — CHA-267 PR-3. The exemption RECORDS table, extracted VERBATIM from
 * CpscExemptions (CHA-222/225/227) so Register (Ruling 4) and the Exemptions surface render the SAME
 * table — one implementation of the row actions and their guards, never a fork (§8.2):
 *  - Edit hidden for MANUAL/declared records (Stage-1 gate #30 — CLIENT-ONLY: an engine re-save would
 *    silently destroy the declaration basis) and shown ONLY when the host passes `onEdit` (the fabric
 *    form lives on the Exemptions surface; Register omits it).
 *  - Revoke hidden when already REVOKED (#31).
 *  - Download/Email visible only for records WITH evidence (#32), and BOTH actions tenant-path-guarded
 *    (#27/#28 — refuse any storagePath outside cpsc_exemptions/{tenantId}/, defense-in-depth per §7).
 *  - Errors surfaced, never swallowed (§8.3; #55).
 * Owns its own subscription + action state. `styleFilter` (optional) narrows rows by style substring —
 * additive, display-only (Register's filter bar).
 */
const s = (v) => (v == null ? '' : String(v));

export default function CpscExemptionRecords({ userProfile, onEdit, styleFilter = '' } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [records, setRecords] = useState([]);
  const [recordsError, setRecordsError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let unsub;
    try {
      const db = getFirestore();
      unsub = onSnapshot(collection(db, 'cpsc_exemptions', tenantId, 'exemptions'), (snap) => {
        setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRecordsError(null);
      }, (err) => {
        console.error('cpsc_exemptions read failed', err);
        setRecordsError('Could not load existing exemption records (check console).');
      });
    } catch (err) { console.error('cpsc_exemptions subscribe failed', err); setRecordsError('Exemption records unavailable (check console).'); }
    return () => { if (unsub) unsub(); };
  }, [isAdmin, tenantId]);

  const revoke = useCallback(async (styleNo) => {
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await httpsCallable(getFunctions(), 'saveCpscExemption')(tenantArg({ operation: 'revoke', styleNo }));
      setResult(res.data);
    } catch (e) { setError(e?.message || 'Could not revoke.'); } finally { setBusy(false); }
  }, [tenantArg]);

  const evidenceOf = useCallback((rec) => (Array.isArray(rec?.evidence) ? rec.evidence.filter((e) => e && e.storagePath) : []), []);
  const isTenantEvidencePath = useCallback((p) => String(p || '').startsWith(`cpsc_exemptions/${tenantId}/`), [tenantId]);

  const downloadEvidence = useCallback(async (storagePath) => {
    setDownloadError(null);
    if (!isTenantEvidencePath(storagePath)) {
      setDownloadError('Refusing to open evidence outside this tenant\'s storage path.');
      return;
    }
    try {
      const url = await getDownloadURL(storageRef(getStorage(), storagePath));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('evidence download failed', err);
      setDownloadError(`Could not download evidence (${err?.code || err?.message || 'unknown error'}).`);
    }
  }, [isTenantEvidencePath]);

  const emailEvidence = useCallback(async (rec) => {
    if (evidenceOf(rec).some((ev) => !isTenantEvidencePath(ev.storagePath))) {
      setError('Refusing to email evidence outside this tenant\'s storage path.');
      return;
    }
    const recipient = window.prompt(`Email the evidence for ${rec.styleNo} to (recipient email):`);
    if (recipient == null) return; // cancelled
    const to = recipient.trim();
    if (!to) { setError('A recipient email is required.'); return; }
    const message = (window.prompt('Optional message to include (leave blank to skip):') || '').trim();
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await httpsCallable(getFunctions(), 'emailExemptionEvidence')(tenantArg({
        styleNo: rec.styleNo, recipient: to, message: message || undefined,
      }));
      setResult({ operation: 'email', styleNo: rec.styleNo, ...res.data });
    } catch (e) { setError(e?.message || 'Could not email the evidence.'); } finally { setBusy(false); }
  }, [tenantArg, evidenceOf, isTenantEvidencePath]);

  if (!isAdmin) return null;

  const filter = s(styleFilter).trim().toLowerCase();
  // Filter across the displayed identifiers (style + reportNo) — same contract as the links table (Codex P2).
  const visible = filter
    ? records.filter((r) => `${s(r.styleNo)} ${s(r.reportNo)}`.toLowerCase().includes(filter))
    : records;

  return (
    <div className="font-plex text-carbon-ink bg-carbon-white border border-carbon-border p-3">
      <h4 className="font-plex-mono text-[10px] uppercase tracking-[0.12em] font-semibold text-carbon-gray-70 mb-2">Exemption records ({visible.length}{filter ? ` of ${records.length}` : ''})</h4>
      {error && <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
      {result && (
        <div className="text-xs text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-2 py-1 mb-2 flex items-center gap-1">
          <CheckCircle2 size={12} className="text-carbon-green-50" />
          <span>{result.operation === 'revoke' ? <>Exemption for <strong>{result.styleNo}</strong> revoked.</>
            : result.operation === 'email' ? <>Evidence for <strong>{result.styleNo}</strong> emailed{result.filename ? <> (<code>{result.filename}</code>)</> : ''}.</>
              : <>Done.</>}</span>
        </div>
      )}
      {recordsError && (
        <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {recordsError}</div>
      )}
      {downloadError && (
        <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {downloadError}</div>
      )}
      {visible.length === 0 ? (
        <p className="text-xs text-carbon-gray-50">{filter ? 'No exemption records match the filter.' : 'No exemption records yet.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-carbon-gray-10"><tr className="text-left font-plex-mono text-[10px] uppercase tracking-[0.12em] font-normal text-carbon-gray-50 border-b border-carbon-border">
              <th className="py-1 pr-2">Style</th><th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Determination</th><th className="py-1 pr-2">Status</th><th className="py-1 pr-2">Actions</th>
            </tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-carbon-gray-10">
                  <td className="py-1 pr-2 font-plex-mono font-medium text-carbon-ink">{r.styleNo}</td>
                  <td className="py-1 pr-2">{r.productType === 'CHILDRENS' ? "Children's" : (r.productType === 'ADULT' ? 'Adult' : '—')}</td>
                  <td className="py-1 pr-2">{r.exemptionPath === 'NOT_SUBJECT' ? <span className="text-carbon-gray-80 font-medium">NOT SUBJECT (no CPSC rule)</span> : r.exempt ? <span className="text-carbon-teal-60 font-medium">{r.exemptionPath === 'MANUAL' ? 'EXEMPT (Declared)' : `EXEMPT (Path ${r.exemptionPath})`}</span> : <span className="font-plex-mono text-carbon-gray-70">NOT EXEMPT</span>}</td>
                  <td className="py-1 pr-2">{r.status === 'REVOKED' ? <span className="font-plex-mono text-carbon-red-60">REVOKED</span> : <span className="font-plex-mono text-carbon-green-50">ACTIVE</span>}</td>
                  <td className="py-1 pr-2">
                    <div className="flex items-center gap-2">
                      {/* Edit loads the fabric form; a MANUAL/declared record has no fabric data, so editing it
                          there would recompute NOT-EXEMPT and silently drop the declaration. Hide Edit for
                          declared records AND not-subject records (CHA-538: same hazard — the fabric form would
                          recompute NOT-EXEMPT and silently drop the determination) AND when the host offers
                          no edit form (Register). Revoke stays available. */}
                      {onEdit && r.exemptionPath !== 'MANUAL' && r.exemptionPath !== 'NOT_SUBJECT' && <button onClick={() => onEdit(r)} className="flex items-center gap-0.5 text-carbon-gray-70 hover:text-carbon-blue-60" aria-label={`edit ${r.styleNo}`}><Pencil size={12} /> Edit</button>}
                      {r.status !== 'REVOKED' && <button onClick={() => revoke(r.styleNo)} disabled={busy} className="flex items-center gap-0.5 text-carbon-gray-70 hover:text-carbon-red-60 disabled:opacity-50" aria-label={`revoke ${r.styleNo}`}><Ban size={12} /> Revoke</button>}
                      {evidenceOf(r).map((ev, i) => (
                        <button key={ev.storagePath} onClick={() => downloadEvidence(ev.storagePath)} className="flex items-center gap-0.5 text-carbon-gray-70 hover:text-carbon-blue-60" aria-label={`download evidence ${i + 1} for ${r.styleNo}`}><Download size={12} /> Download{evidenceOf(r).length > 1 ? ` ${i + 1}` : ''}</button>
                      ))}
                      {evidenceOf(r).length > 0 && <button onClick={() => emailEvidence(r)} disabled={busy} className="flex items-center gap-0.5 text-carbon-gray-70 hover:text-carbon-blue-60 disabled:opacity-50" aria-label={`email evidence for ${r.styleNo}`}><Mail size={12} /> Email</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
